import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Chip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  AppButton,
  AppCancelButton,
  AppSaveButton,
} from '../../../../components/designe/buttons/app-button/AppButton';
import AppDialog from '../../../../components/reusable/app-dialog/AppDialog';
import { useStyles } from './ExerciseFiltersModal.styles';
import {
  IExerciseFilterOption,
  IExerciseFilters,
  cloneExerciseFilters,
  getAppliedExerciseFiltersCount,
} from './helpers/types';

interface IExerciseFiltersModalProps {
  open: boolean;
  filters: IExerciseFilters;
  categories: IExerciseFilterOption[];
  muscles: IExerciseFilterOption[];
  equipment: IExerciseFilterOption[];
  developedSkills: IExerciseFilterOption[];
  onChange: (value: IExerciseFilters) => void;
  onCancel: () => void;
  onReset: () => void;
  onApply: () => void;
}

function toggleCriterionValue(values: string[], optionId: string): string[] {
  if (values.includes(optionId)) {
    return values.filter((value) => value !== optionId);
  }

  return [...values, optionId];
}

export function ExerciseFiltersModal({
  open,
  filters,
  categories,
  muscles,
  equipment,
  developedSkills,
  onChange,
  onCancel,
  onReset,
  onApply,
}: IExerciseFiltersModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const selectedCount = getAppliedExerciseFiltersCount(filters);

  function handleCriterionToggle(
    criterion: 'categories' | 'muscles' | 'equipment' | 'developedSkills',
    optionId: string,
  ) {
    const nextFilters = cloneExerciseFilters(filters);
    nextFilters[criterion] = toggleCriterionValue(
      nextFilters[criterion],
      optionId,
    );
    onChange(nextFilters);
  }

  function renderSection(
    criterion: 'categories' | 'muscles' | 'equipment' | 'developedSkills',
    title: string,
    options: IExerciseFilterOption[],
  ) {
    return (
      <Box className={classes.section}>
        <Typography variant="text-md" className={classes.sectionTitle}>
          {title}
        </Typography>
        <Box className={classes.chips}>
          {options.map((option) => {
            const isSelected = filters[criterion].includes(option.id);
            return (
              <Chip
                key={option.id}
                label={option.name}
                color={isSelected ? 'primary' : 'default'}
                variant="filled"
                className={
                  isSelected ? classes.chipSelected : classes.chipUnselected
                }
                clickable={true}
                onClick={() => handleCriterionToggle(criterion, option.id)}
              />
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <AppDialog
      open={open}
      onClose={onCancel}
      title={t('exercises.filters.title', {
        defaultValue: 'Exercise filters',
      })}>
      <Box className={classes.content}>
        <Typography variant="text-sm" className={classes.selectedCountText}>
          {t('exercises.filters.selectedCount', {
            defaultValue: '{{count}} selected',
            count: selectedCount,
          })}
        </Typography>

        {renderSection(
          'categories',
          t('exercises.filters.categories', { defaultValue: 'Categories' }),
          categories,
        )}
        {renderSection(
          'muscles',
          t('exercises.filters.muscles', { defaultValue: 'Muscles' }),
          muscles,
        )}
        {renderSection(
          'equipment',
          t('exercises.filters.equipment', { defaultValue: 'Equipment' }),
          equipment,
        )}
        {renderSection(
          'developedSkills',
          t('exercises.filters.developedSkills', {
            defaultValue: 'Developed skills',
          }),
          developedSkills,
        )}

        <Box className={classes.actions}>
          <AppCancelButton onClick={onCancel}>
            {t('common.cancel')}
          </AppCancelButton>
          <AppButton variant="outlined" onClick={onReset}>
            {t('exercises.filters.reset', { defaultValue: 'Reset' })}
          </AppButton>
          <AppSaveButton onClick={onApply}>
            {t('exercises.filters.apply', { defaultValue: 'Apply' })}
          </AppSaveButton>
        </Box>
      </Box>
    </AppDialog>
  );
}
